/**
 * main.js — general layouts (home, landing, archive, search, multimedia, …).
 *
 * Loaded WITHOUT `defer` from View/Layouts/head/head.ctp, so it runs mid-parse;
 * everything is deferred to onReady().
 *
 * Every feature is a module in MODULES and runs inside safe(), so one missing
 * element or one throwing third-party global cannot take down the rest of the
 * page's JS.
 */
(function () {
  "use strict";

  // ===== SHARED PRELUDE — byte-identical in main.js, main-news.js,
  // main-video-news.js and video-news.js. These are classic scripts loaded on
  // different pages, so the block is duplicated rather than imported;
  // build/js.js fails the build if the copies drift. =====

  /** Run one module in isolation: a throw here must not stop the others. */
  function safe(name, fn) {
    try {
      fn();
    } catch (err) {
      if (window.console && console.error) {
        console.error("[" + SCRIPT_NAME + '] module "' + name + '" failed:', err);
      }
    }
  }

  function onReady(fn) {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", fn);
    } else {
      fn();
    }
  }

  function qs(sel, root) {
    return (root || document).querySelector(sel);
  }

  function qsa(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  /* The header panels are shown/hidden through inline opacity + visibility, and
     the "is it open?" test reads the INLINE style (not the computed one). Kept
     exactly as-is: a computed-style test would report "hidden" for a panel that
     has never been toggled, flipping the first click. */
  function isShown(el) {
    return el.style.opacity === "1";
  }

  function show(el) {
    el.style.opacity = "1";
    el.style.visibility = "visible";
  }

  function hide(el) {
    el.style.opacity = "0";
    el.style.visibility = "hidden";
  }

  function toggleShown(el) {
    if (isShown(el)) hide(el);
    else show(el);
  }

  function initSearchToggle() {
    var icon = qs(".main_header .search_icon");
    if (!icon) return;
    icon.addEventListener("click", function (e) {
      e.preventDefault();
      e.stopPropagation();
      var form = qs(".main_header #search_container");
      if (form) toggleShown(form);
    });
  }

  function initCloseSearch() {
    var button = document.getElementById("close_search");
    if (!button) return;
    button.addEventListener("click", function () {
      var form = document.getElementById("search_container");
      if (form) hide(form);
    });
  }

  /* Mobile menu + submenu are gated on screen width (the physical screen, not
     the viewport — window.screen.width, as in the original). */
  function isMobileWidth() {
    return window.screen.width < 800;
  }

  function initMobileMenu() {
    if (!isMobileWidth()) return;
    var button = qs(".main_nav .show_menu");
    if (!button) return;
    button.addEventListener("click", function () {
      var menu = qs(".main_menu");
      if (menu) toggleShown(menu);
    });
  }

  function initSubmenu() {
    if (!isMobileWidth()) return;
    qsa(".submenu_mobile_icn").forEach(function (icon) {
      icon.addEventListener("click", function (e) {
        e.stopPropagation();
        e.preventDefault();
        var panel = this.parentNode && this.parentNode.children[2];
        if (!panel) return;
        panel.style.display = panel.style.display === "block" ? "none" : "block";
      });
    });
  }

  function initSecondMenu() {
    var button = qs(".show_second_menu");
    if (!button) return;
    button.addEventListener("click", function () {
      var menu = qs(".second_menu");
      if (menu) toggleShown(menu);
    });
  }

  /** Sticky mobile ad rail: reveal past `threshold`, dismiss on the close button. */
  function initStickyAd(parentId, closeId, threshold) {
    var parent = document.getElementById(parentId);
    if (!parent) return;
    window.addEventListener("scroll", function () {
      if (window.pageYOffset > threshold) parent.classList.add("show");
      else parent.classList.remove("show");
    });
    var close = document.getElementById(closeId);
    if (!close) return;
    close.addEventListener("click", function () {
      // assigning a string to .classList replaces the whole class list
      parent.classList = "none_important";
    });
  }

  /* `probeSelector` differs per page and is passed in ON PURPOSE: main.js probes
     "#close_emergency", the two article scripts probe "close_emergency" with no
     "#", which matches nothing — so the emergency-bar close button has never
     worked on article pages. Preserved verbatim; making it fire would be a
     behaviour change, not a refactor. */
  function initCloseEmergency(probeSelector) {
    if (!qs(probeSelector)) return;
    var button = document.getElementById("close_emergency");
    if (!button) return;
    button.addEventListener("click", function () {
      var bar = document.getElementById("emergency_parent");
      if (bar) hide(bar);
    });
  }

  /** Drop the skeleton placeholder once an ad slot receives markup. */
  function initAdSkeletons() {
    qsa(".type-script").forEach(function (container) {
      if (!container.classList.contains("ad-skeleton")) {
        container.classList.add("ad-skeleton");
      }
      var observer = new MutationObserver(function (mutations) {
        mutations.forEach(function (mutation) {
          if (mutation.type === "childList" && mutation.target.innerHTML.trim() !== "") {
            container.classList.remove("ad-skeleton");
          }
        });
      });
      observer.observe(container, { childList: true, subtree: true });
    });
  }
  // ===== END SHARED PRELUDE =====

  var SCRIPT_NAME = "main.js";

  // ---------------------------------------------------------------- helpers --

  /** Tab strip: clicking the Nth title activates the Nth content pane. */
  function initTabs(titleSelector, contentSelector) {
    var titles = qsa(titleSelector);
    if (!titles.length) return;
    titles.forEach(function (title) {
      title.addEventListener("click", function () {
        if (title.classList.contains("active")) return;
        var index = titles.indexOf(title);
        titles.forEach(function (other) {
          other.classList.remove("active");
        });
        title.classList.add("active");
        qsa(contentSelector).forEach(function (pane) {
          pane.classList.remove("active");
        });
        var pane = qs(contentSelector + ":nth-child(" + (index + 1) + ")");
        if (pane) pane.classList.add("active");
      });
    });
  }

  /** Type-ahead style list search shared by the company and author directories. */
  function initDirectorySearch(options) {
    var input = document.getElementById(options.inputId);
    if (!input) return;

    function run(term) {
      fetch(options.endpoint + encodeURIComponent(term))
        .then(function (response) {
          if (response.ok) return response.json();
          throw new Error(
            "Request failed for " + options.label + ". Returned status of " + response.status
          );
        })
        .then(function (items) {
          var list = document.getElementById(options.listId);
          if (!list) return;
          if (!items.length) {
            list.innerHTML = '<div class="fade-in">نتیجه‌ای یافت نشد</div>';
            return;
          }
          list.innerHTML = items
            .map(function (item) {
              return (
                '<li class="fade-in">' +
                "<a href='" + item.url + "' target='_blank' class='image'>" +
                "<img loading='lazy' alt='" + item.title + "' src='" + item.image + "' />" +
                "</a>" +
                "<a class='title' href='" + item.url + "' title='" + item.title +
                "' target='_blank'>" + item.title + "</a>" +
                "</li>"
              );
            })
            .join("");
        })
        .catch(function (error) {
          console.error(error);
        });
    }

    input.addEventListener("keydown", function (event) {
      if (event.key !== "Enter") return;
      event.preventDefault(); // Prevent the default form submission
      run(input.value);
    });

    var button = qs(options.buttonSelector);
    if (button) {
      button.addEventListener("click", function () {
        run(input.value);
      });
    }
  }

  var WEATHER_CITIES = {
    112931: { title: "تهران" },
    112646: { title: "تربت حیدریه" },
    124665: { title: "مشهد" },
    418863: { title: "اصفهان" },
    115019: { title: "شیراز" },
    128747: { title: "کرج" },
    113646: { title: "تبریز" },
    130802: { title: "ایلام" },
    143127: { title: "اراک" },
    128234: { title: "کرمان" },
    116402: { title: "سمنان" },
    111453: { title: "زنجان" },
    1159301: { title: "زاهدان" },
    1161724: { title: "چابهار" },
    143083: { title: "اردبیل" },
    119505: { title: "قزوین" },
    139817: { title: "بندر بوشهر" },
    128226: { title: "کرمانشاه" },
    140918: { title: "بهشهر" },
    118743: { title: "رشت" },
    118994: { title: "رفسنجان" },
    142363: { title: "بابل" },
    132144: { title: "همدان" },
    418606: { title: "نجف آباد" },
    116667: { title: "ساوه" },
    132892: { title: "گرگان" },
    111822: { title: "یزد" },
    119208: { title: "قم" },
    119374: { title: "قشم" },
    1113217: { title: "زابل" },
    124862: { title: "مرند" },
    114259: { title: "سیرجان" },
    124085: { title: "میان دو آب" },
    1160571: { title: "خاش" },
    124291: { title: "مهران" },
    114584: { title: "شوشتر" },
    126972: { title: "خوی" },
    117574: { title: "سنندج" },
    117656: { title: "سلماس" },
    121380: { title: "پارس بند" },
    128831: { title: "کنگاور" },
    122397: { title: "نکا" },
    122285: { title: "نیشابور" },
    145459: { title: "آبادان" },
    143921: { title: "الیگودرز" },
    142676: { title: "آستارا" },
    127349: { title: "خرم آباد" },
    127319: { title: "خرم شهر" },
    118367: { title: "رباط کریم" },
    125897: { title: "لنگرود" },
    128321: { title: "کازرون" },
    119161: { title: "قروه" },
    132938: { title: "گنبد کاووس" },
    129933: { title: "جویبار" },
    140380: { title: "بجنورد" },
    141679: { title: "بندر انزلی" },
    137268: { title: "سوسنگرد" }
  };

  // ---------------------------------------------------------------- modules --

  var MODULES = {
    searchToggle: initSearchToggle,

    closeSearch: initCloseSearch,

    archivePanels: function () {
      qsa(".archive_news_type").forEach(function (archive) {
        archive.addEventListener("click", function (event) {
          if (!event.target.closest("label")) return;
          var panel = this.querySelector(".panel");
          if (panel) panel.classList.toggle("active");
        });
      });
    },

    /* Sticky header with a placeholder element that takes over the header's
       height so the page below does not jump when it detaches. */
    stickyHeader: function () {
      var header = qs("#main_header");
      if (!header) return;
      window.addEventListener("scroll", function () {
        var placeholder = qs(".header-placeholder");
        if (!placeholder) {
          placeholder = document.createElement("div");
          placeholder.classList.add("header-placeholder");
          header.parentNode.insertBefore(placeholder, header.nextSibling);
        }
        if (window.scrollY > 0) {
          header.classList.add("sticky");
          placeholder.style.height = header.offsetHeight + "px";
        } else {
          header.classList.remove("sticky");
          placeholder.style.height = "0px";
        }
      });
    },

    mobileMenu: initMobileMenu,

    submenu: initSubmenu,

    secondMenu: initSecondMenu,

    tabsFourthRight: function () {
      initTabs(".tab_box #fourth_title_tab_right li", "#fourth_content_tab_right > div");
    },

    tabsFourthLeft: function () {
      initTabs(".tab_box #fourth_title_tab_left li", "#fourth_content_tab_left > div");
    },

    /* Fading video slider driven by an external thumbnail strip. */
    videoSlider: function () {
      if (typeof Splide === "undefined") return;
      if (!qs(".splide")) return;

      var slider = new Splide(".splide", {
        loop: true,
        rewind: true,
        autoplay: true,
        type: "fade",
        adaptiveHeight: false
      });
      slider.mount();

      var thumbnails = document.getElementsByClassName("thumbnail");
      var current = null;

      Array.prototype.forEach.call(thumbnails, function (thumbnail, index) {
        thumbnail.addEventListener("click", function () {
          slider.go(index);
        });
      });

      var strip = document.getElementById("thumbnails");
      var first = strip && strip.children[0];
      if (first) first.classList.add("is-active");

      slider.on("move", function () {
        if (first) first.classList.remove("is-active");
        if (current) current.classList.remove("is-active");
        var thumbnail = thumbnails[slider.index];
        if (thumbnail) {
          thumbnail.classList.add("is-active");
          current = thumbnail;
        }
      });
    },

    multimediaSlider: function () {
      if (typeof Splide === "undefined") return;
      if (!document.getElementById("multimedia_top")) return;
      new Splide("#multimedia_top", {
        autoplay: true,
        interval: 4000,
        type: "loop",
        direction: "rtl",
        adaptiveHeight: false
      }).mount();
    },

    tabsFooter: function () {
      initTabs(".tab_box #footer_title_tab li", "#footer_content_tab > div");
    },

    archiveFlip: function () {
      var flip = qs(".archive_news_type .flip");
      if (!flip) return;
      flip.addEventListener("click", function () {
        var panel = qs(".archive_news_type .panel");
        if (!panel) return;
        if (panel.style.display === "block") {
          panel.style.display = "none";
          flip.classList.remove("flip_toggle");
        } else {
          flip.classList.add("flip_toggle");
          panel.style.display = "block";
        }
      });
    },

    advStickyMobile: function () {
      initStickyAd("adv_mobile_sticky", "close_z0", 600);
    },

    advStickyMobileTop: function () {
      initStickyAd("adv_mobile_sticky_top", "close_z1", 600);
    },

    closeEmergency: function () {
      initCloseEmergency("#close_emergency");
    },

    prayerTime: function () {
      var target = qs("#prayerTime");
      if (!target || typeof Prayer === "undefined") return;
      Prayer.getTimes(target, 8);
      var city = document.getElementById("selectPrayerTimeCity");
      if (!city) return;
      city.addEventListener("change", function () {
        Prayer.getTimes(target, this.value);
      });
    },

    weather: function () {
      var target = qs("#weatherNew");
      if (!target || typeof Weather === "undefined") return;
      Weather.getWeather(target, WEATHER_CITIES);
    },

    companySearch: function () {
      initDirectorySearch({
        inputId: "company-search-input",
        listId: "company-author-list",
        buttonSelector: ".search_company",
        endpoint: "/companies/ajaxSearch?q=",
        label: "ajax company search"
      });
    },

    authorSearch: function () {
      initDirectorySearch({
        inputId: "author-search-input",
        listId: "author-list",
        buttonSelector: ".search_author",
        endpoint: "/authors/ajaxSearch?q=",
        label: "ajax author search"
      });
    },

    adSkeletons: initAdSkeletons
  };

  onReady(function () {
    Object.keys(MODULES).forEach(function (name) {
      safe(name, MODULES[name]);
    });
  });
})();
